import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { OdooTask } from '@/types/odoo';
import { fetchSiblings, saveMultiday } from '@/api/planning';
import type { SiblingTask } from '@/api/planning';
import { usePlanningStore } from '@/stores/planning';
import { formatDateKey, getDayLabel, getISOWeekNumber, isToday } from '@/utils/planning';
import { displayProjectName } from '@/utils/format';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';

interface MultiDayPopupProps {
  task: OdooTask;
  onClose: () => void;
  onSaved: () => void;
}

type ViewMode = 'weeks' | 'month';

const DUTCH_MONTHS_FULL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
] as const;

/** Get Monday of a given date's week. */
function getMondayOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

/** Build Mon-Sat dates for a week starting from the given Monday. */
function getWeekDaysFromMonday(monday: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

interface WeekRow {
  weekNumber: number;
  days: Date[];
}

export function MultiDayPopup({ task, onClose, onSaved }: MultiDayPopupProps) {
  const [siblings, setSiblings] = useState<SiblingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [originalDates, setOriginalDates] = useState<Set<string>>(new Set());
  const [dateToTaskId, setDateToTaskId] = useState<Map<string, number>>(new Map());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [weekOffset, setWeekOffset] = useState(() => {
    const taskDate = task.date_deadline ? new Date(task.date_deadline.slice(0, 10) + 'T12:00:00') : new Date();
    const todayMonday = getMondayOfWeek(new Date());
    const taskMonday = getMondayOfWeek(taskDate);
    return Math.round((taskMonday.getTime() - todayMonday.getTime()) / (7 * 86_400_000));
  });
  const [monthOffset, setMonthOffset] = useState(() => {
    const taskDate = task.date_deadline ? new Date(task.date_deadline.slice(0, 10) + 'T12:00:00') : new Date();
    const now = new Date();
    return (taskDate.getFullYear() - now.getFullYear()) * 12 + (taskDate.getMonth() - now.getMonth());
  });

  const userMap = usePlanningStore(s => s.userMap);
  const projectNameMap = usePlanningStore(s => s.projectNameMap);

  const soId = task.sale_order_id ? task.sale_order_id[0] : 0;
  const projectName = projectNameMap.get(soId) || (task.project_id ? displayProjectName(task.project_id[1]) : 'Geen project');

  const installerNames = (task.x_studio_installateur || [])
    .map(id => userMap.get(id))
    .filter(Boolean)
    .join(', ') || 'Geen installateurs';

  const parentId = task.parent_id ? task.parent_id[0] : 0;

  // Today's date key for highlight
  const todayKey = formatDateKey(new Date());

  // Fetch siblings on mount
  useEffect(() => {
    if (!parentId) {
      setLoading(false);
      setError('Taak heeft geen bovenliggende fase');
      return;
    }
    setLoading(true);
    setError(null);
    fetchSiblings(parentId)
      .then(result => {
        setSiblings(result.siblings);
        const origDates = new Set<string>();
        const dtMap = new Map<string, number>();
        for (const sib of result.siblings) {
          if (sib.date_deadline) {
            const dk = sib.date_deadline.slice(0, 10);
            origDates.add(dk);
            dtMap.set(dk, sib.id);
          }
        }
        setOriginalDates(origDates);
        setDateToTaskId(dtMap);
        setSelectedDates(new Set(origDates));
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Kan taken niet laden');
      })
      .finally(() => setLoading(false));
  }, [parentId]);

  // Escape key closes
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Build week rows for the week view calendar
  const weekRows = useMemo<WeekRow[]>(() => {
    const now = new Date();
    const currentMonday = getMondayOfWeek(now);

    // Current week + 2 more weeks = 3 total minimum
    const mondaySet = new Map<string, Date>();
    for (let i = 0; i < 3; i++) {
      const mon = new Date(currentMonday);
      mon.setDate(currentMonday.getDate() + i * 7);
      mondaySet.set(formatDateKey(mon), mon);
    }

    // Include weeks for all sibling dates
    const allDates = new Set([...selectedDates, ...originalDates]);
    for (const dk of allDates) {
      const d = new Date(dk + 'T12:00:00');
      const mon = getMondayOfWeek(d);
      mondaySet.set(formatDateKey(mon), mon);
    }

    // Apply week offset: add extra weeks before/after
    const sortedMondaysBase = [...mondaySet.values()].sort((a, b) => a.getTime() - b.getTime());
    if (sortedMondaysBase.length > 0 && weekOffset !== 0) {
      if (weekOffset < 0) {
        // Add weeks before the first
        const first = sortedMondaysBase[0];
        for (let i = weekOffset; i < 0; i++) {
          const mon = new Date(first);
          mon.setDate(first.getDate() + i * 7);
          mondaySet.set(formatDateKey(mon), mon);
        }
      } else {
        // Add weeks after the last
        const last = sortedMondaysBase[sortedMondaysBase.length - 1];
        for (let i = 1; i <= weekOffset; i++) {
          const mon = new Date(last);
          mon.setDate(last.getDate() + i * 7);
          mondaySet.set(formatDateKey(mon), mon);
        }
      }
    }

    // Sort mondays chronologically
    const sortedMondays = [...mondaySet.values()].sort((a, b) => a.getTime() - b.getTime());

    // Fill any gaps between first and last Monday
    const rows: WeekRow[] = [];
    if (sortedMondays.length > 0) {
      const first = sortedMondays[0];
      const last = sortedMondays[sortedMondays.length - 1];
      const current = new Date(first);
      while (current <= last) {
        const days = getWeekDaysFromMonday(new Date(current));
        rows.push({
          weekNumber: getISOWeekNumber(current),
          days,
        });
        current.setDate(current.getDate() + 7);
      }
    }

    return rows;
  }, [selectedDates, originalDates, weekOffset]);

  // Build month grid for month view
  const monthData = useMemo(() => {
    const now = new Date();
    const targetMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = targetMonth.getFullYear();
    const month = targetMonth.getMonth();
    const monthName = DUTCH_MONTHS_FULL[month];

    // Find the Monday on or before the 1st of the month
    const firstOfMonth = new Date(year, month, 1);
    const startMonday = getMondayOfWeek(firstOfMonth);

    // Find the last day of the month
    const lastOfMonth = new Date(year, month + 1, 0);
    // Find the Saturday on or after the last day of the month
    const endDate = new Date(lastOfMonth);
    const endDay = endDate.getDay();
    // We need to reach Saturday (6)
    if (endDay !== 6) {
      const daysToSat = endDay === 0 ? 6 : 6 - endDay;
      endDate.setDate(endDate.getDate() + daysToSat);
    }

    // Build week rows from startMonday through endDate
    const rows: WeekRow[] = [];
    const current = new Date(startMonday);
    while (current <= endDate) {
      const days = getWeekDaysFromMonday(new Date(current));
      rows.push({
        weekNumber: getISOWeekNumber(current),
        days,
      });
      current.setDate(current.getDate() + 7);
    }

    return { year, month, monthName, rows };
  }, [monthOffset]);

  // Toggle a date
  const toggleDate = useCallback((dk: string) => {
    setSelectedDates(prev => {
      const next = new Set(prev);
      if (next.has(dk)) {
        next.delete(dk);
      } else {
        next.add(dk);
      }
      return next;
    });
  }, []);

  // Diff computation
  const newDates = useMemo(
    () => [...selectedDates].filter(d => !originalDates.has(d)).sort(),
    [selectedDates, originalDates],
  );
  const removedDates = useMemo(
    () => [...originalDates].filter(d => !selectedDates.has(d)),
    [selectedDates, originalDates],
  );
  const removeTaskIds = useMemo(
    () => removedDates.map(d => dateToTaskId.get(d)!).filter(Boolean),
    [removedDates, dateToTaskId],
  );
  const hasDiff = newDates.length > 0 || removeTaskIds.length > 0;

  // The date of the clicked task
  const taskDateKey = task.date_deadline ? task.date_deadline.slice(0, 10) : '';

  // Save handler
  async function handleSave() {
    if (!hasDiff) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await saveMultiday({
        source_task_id: task.id,
        parent_id: parentId,
        new_dates: newDates,
        remove_task_ids: removeTaskIds,
      });
      onSaved();
      if (result.errors && result.errors.length > 0) {
        setError(`${result.errors.length} bewerking(en) mislukt`);
        setSaving(false);
        return;
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      setSaving(false);
    }
  }

  /** Render a single day cell button. */
  function renderDayCell(day: Date, options?: { outsideMonth?: boolean }) {
    const dk = formatDateKey(day);
    const selected = selectedDates.has(dk);
    const isTodayDate = isToday(day);
    const isCurrentTask = dk === taskDateKey;
    const outsideMonth = options?.outsideMonth ?? false;

    if (outsideMonth) {
      return (
        <div
          key={dk}
          className="rounded-lg py-2 px-1 text-center text-xs bg-gray-50 text-gray-300"
        >
          <span className="block">{day.getDate()}</span>
        </div>
      );
    }

    return (
      <button
        key={dk}
        type="button"
        onClick={() => toggleDate(dk)}
        className={[
          'rounded-lg py-2 px-1 text-center text-xs transition-colors cursor-pointer',
          selected
            ? 'bg-teal-500 text-white font-semibold'
            : 'bg-white border border-gray-200 text-td-navy hover:bg-gray-50',
          isTodayDate ? 'ring-2 ring-td-blue' : '',
        ].join(' ')}
      >
        <span className="block">{viewMode === 'weeks' ? getDayLabel(day) : day.getDate()}</span>
        {isCurrentTask && (
          <span className={`block text-[9px] mt-0.5 ${selected ? 'text-white/80' : 'text-td-muted'}`}>
            (huidig)
          </span>
        )}
      </button>
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Meerdaagse planning"
      className="fixed inset-0 z-50 flex items-center justify-center"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Content panel */}
      <div className="relative z-10 bg-white rounded-xl shadow-2xl max-w-xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="text-base font-bold text-td-navy">Meerdere dagen</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-td-muted hover:text-td-navy"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Task info */}
        <div className="px-5 pb-3">
          <p className="text-sm font-semibold text-td-navy truncate">{task.isInterventie && <InterventieBadge className="mr-2 align-middle" />}{projectName} — {task.name}</p>
          <p className="text-xs text-td-muted mt-0.5">{installerNames} &middot; Selecteer de dagen</p>
        </div>

        {/* View mode toggle */}
        <div className="px-5 pb-4 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setViewMode('weeks')}
            className={[
              'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
              viewMode === 'weeks' ? 'bg-td-blue text-white' : 'bg-gray-100 text-td-muted',
            ].join(' ')}
          >
            Weken
          </button>
          <button
            type="button"
            onClick={() => setViewMode('month')}
            className={[
              'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
              viewMode === 'month' ? 'bg-td-blue text-white' : 'bg-gray-100 text-td-muted',
            ].join(' ')}
          >
            Maand
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="px-5 pb-6 text-center">
            <div
              className="inline-block w-6 h-6 border-3 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
              role="status"
            >
              <span className="sr-only">Laden...</span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-5 mb-4 bg-red-50 text-red-600 p-3 rounded-lg text-sm" role="alert">
            {error}
          </div>
        )}

        {/* Calendar — Week View */}
        {!loading && viewMode === 'weeks' && (
          <div className="px-5 pb-4 space-y-3">
            {/* Week navigation arrows */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setWeekOffset(prev => prev - 1)}
                className="p-1 text-td-muted hover:text-td-navy"
                aria-label="Vorige weken"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-xs text-td-muted font-semibold">
                Week {weekRows[0]?.weekNumber} – {weekRows[weekRows.length - 1]?.weekNumber}
              </span>
              <button
                type="button"
                onClick={() => setWeekOffset(prev => prev + 1)}
                className="p-1 text-td-muted hover:text-td-navy"
                aria-label="Volgende weken"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {weekRows.map(week => (
              <div key={formatDateKey(week.days[0])}>
                <p className="text-xs font-bold text-td-muted mb-1.5">Week {week.weekNumber}</p>
                <div className="grid grid-cols-6 gap-1.5">
                  {week.days.map(day => renderDayCell(day))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Calendar — Month View */}
        {!loading && viewMode === 'month' && (
          <div className="px-5 pb-4 space-y-3">
            {/* Month navigation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setMonthOffset(prev => prev - 1)}
                  className="p-1 text-td-muted hover:text-td-navy"
                  aria-label="Vorige maand"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setMonthOffset(0)}
                  disabled={monthOffset === 0}
                  className="px-2.5 py-1 text-xs font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Vandaag
                </button>
              </div>
              <span className="text-sm font-bold text-td-navy capitalize">
                {monthData.monthName} {monthData.year}
              </span>
              <button
                type="button"
                onClick={() => setMonthOffset(prev => prev + 1)}
                className="p-1 text-td-muted hover:text-td-navy"
                aria-label="Volgende maand"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Calendar grid — fixed height to prevent layout shift across months */}
            <div className="min-h-[340px] space-y-3">
              {/* Column headers */}
              <div className="grid grid-cols-[auto_repeat(6,1fr)] gap-1.5">
                <span className="pr-1"></span>
                {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'].map(label => (
                  <div key={label} className="text-center text-xs font-bold text-td-muted py-1">
                    {label}
                  </div>
                ))}
              </div>

              {/* Month week rows */}
              {monthData.rows.map(week => (
                <div key={formatDateKey(week.days[0])} className="grid grid-cols-[auto_repeat(6,1fr)] gap-1.5 items-center">
                  <span className="text-xs font-bold text-td-muted pr-1">W{week.weekNumber}</span>
                  {week.days.map(day => {
                    const outsideMonth = day.getMonth() !== monthData.month;
                    return renderDayCell(day, { outsideMonth });
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="px-5 pb-5 flex items-center justify-between gap-3">
            {/* Diff summary */}
            <div className="text-xs">
              {hasDiff ? (
                <span>
                  {newDates.length > 0 && (
                    <span className="text-teal-600 font-semibold">+{newDates.length} {newDates.length === 1 ? 'dag' : 'dagen'}</span>
                  )}
                  {newDates.length > 0 && removedDates.length > 0 && (
                    <span className="text-td-muted">, </span>
                  )}
                  {removedDates.length > 0 && (
                    <span className="text-red-500 font-semibold">&minus;{removedDates.length} {removedDates.length === 1 ? 'dag' : 'dagen'}</span>
                  )}
                </span>
              ) : (
                <span className="text-td-muted">Geen wijzigingen</span>
              )}
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-semibold text-td-muted hover:bg-gray-100 rounded-lg"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasDiff || saving}
                className="px-4 py-2 text-sm font-semibold bg-td-blue text-white rounded-lg hover:bg-td-blue/90 disabled:opacity-50"
              >
                {saving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
